/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import { memo, type ReactNode, useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import styled from '@mui/material/styles/styled';

import { type components } from '@marketplace/gather-api/types/marketplace-vehicle-catalog-mgmt-public';
import { AppSpacing } from '@marketplace/gather-ui/theme/constants';
import { Card } from '@marketplace/gather-ui/components/card';
import CardContent from '@marketplace/gather-ui/components/card-content';
import Typography from '@marketplace/gather-ui/components/typography';

import useSearchOptions from '@/rest/hooks/use-search-options';
import { useSearchDropdown } from '../search-dropdown.provider';
import { type UpdateSearchValueParams } from '../../search.component';

const StyleItem = styled(Box)(() => ({
  flex: '0 0 70%',
  minWidth: 200,
  paddingRight: AppSpacing.SM,
  '@media (min-width:600px)': {
    flex: '0 0 55%',
    minWidth: 220,
  },
  '@media (min-width:960px)': {
    flex: '1 1 auto',
    minWidth: 'unset',
    paddingRight: 0,
  },
}));

const StyleCard = styled(Card)(() => ({
  height: 180,
  backgroundColor: '#fff',
  cursor: 'pointer',
}));

const StyleCardContent = styled(CardContent)(() => ({
  height: '100%',
  padding: AppSpacing.SM,
}));

const StylesContainer = styled(Box)(() => ({
  display: 'flex',
  overflowX: 'auto',
  columnGap: AppSpacing.SM,
  rowGap: AppSpacing.SM,
  paddingBottom: AppSpacing.SM,
  '@media (min-width:960px)': {
    display: 'grid',
    overflowX: 'visible',
    columnGap: AppSpacing.SM,
    rowGap: AppSpacing.SM,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    paddingBottom: 0,
  },
  '@media (min-width:1200px)': {
    columnGap: AppSpacing.MD,
    rowGap: AppSpacing.MD,
    gridTemplateColumns: 'repeat(auto-fit, minmax(223px, 1fr))',
  },
}));

const nameContainerSx = { objectFit: 'contain' };

type BodyTypeStyleProps = {
  selectBodyType(name: string): Promise<void>;
  style: components['schemas']['BodyType'];
};

const BodyTypeStyle = memo(function BodyTypeStyle({
  selectBodyType,
  style,
}: BodyTypeStyleProps): ReactNode {
  const handleClick = useCallback(
    () => style.name && selectBodyType(style.name),
    [selectBodyType, style.name]
  );

  const handleKeyPress = useCallback(
    (keyboardEvent: ReactKeyboardEvent) => {
      keyboardEvent.stopPropagation();
      if (keyboardEvent.key === 'Enter') {
        handleClick();
      }
    },
    [handleClick]
  );

  return (
    <StyleItem>
      <StyleCard tabIndex={0} variant='outlined' onClick={handleClick} onKeyDown={handleKeyPress}>
        <StyleCardContent>
          <Stack alignItems='center' height='100%' justifyContent='center' spacing={1}>
            <Box
              alt={style.name ?? ''}
              component='img'
              height={80}
              maxWidth={160}
              src={style.imageUrl}
              sx={nameContainerSx}
              width='100%'
            />
            <Typography size='small' textAlign='center' variant='body'>
              {style.name}
            </Typography>
          </Stack>
        </StyleCardContent>
      </StyleCard>
    </StyleItem>
  );
});

type QuickBodyTypesProps = {
  updateSearchValue: (options: UpdateSearchValueParams) => Promise<void>;
};

export function QuickBodyTypes({ updateSearchValue }: QuickBodyTypesProps): ReactNode {
  const { closeDropdown } = useSearchDropdown();
  const searchOptionsState = useSearchOptions();

  const bodyTypes = searchOptionsState.data?.vehicleStyles ?? [];

  const selectBodyType = useCallback(
    async (bodyType: string): Promise<void> => {
      await updateSearchValue({
        searchValue: bodyType,
        searchType: 'QUICK_BODY_TYPE',
        selectedFilter: { name: bodyType, imageUrl: '' },
        autoSubmit: true,
      });
      closeDropdown();
    },
    [closeDropdown, updateSearchValue]
  );

  if (bodyTypes.length === 0) {
    return null;
  }

  return (
    <Stack spacing={4}>
      <Typography color='text.primary' size='small' variant='functional'>
        Vehicle Style
      </Typography>

      <StylesContainer>
        {bodyTypes.map((style) => (
          <BodyTypeStyle key={style.name} selectBodyType={selectBodyType} style={style} />
        ))}
      </StylesContainer>
    </Stack>
  );
}
